import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import * as applicationApi from '../../../api/application';
import * as AppConstants from '../../../app/AppConstants';

import { handleCcpPaymentCreate, handleOlppPaymentCreate } from '../../payment/Payment/PaymentSlice'

const initialState = {
  isLoading: true,
  searchInput: {
    searchType: AppConstants.SEARCH_TYPE.SELF,
    netId: null,
    pageSize: 10, // Table Pagination
    pageNum: 0
  },
  searchResult: {
    rows: [],
    totalCount: 0
  },
  selectedRow: {
    details: null,
    ccpUrl: null,
    photoInput: {
      applicationId: null,
      allowReupload: null,
      photoBuf: null
    },
    paymentMethodInput: {
      applicationId: null,
      paymentMethod: null
    }
  },
  isManagePaymentDialogOpen: false,
  isPayInPersonDialogOpen: false,
  isCCPDialogOpen: false,
};

export const dispatchSearchInput = createAsyncThunk(
  'userHistory/searchinput/set',
  async (payload, ThunkAPI) => {
    const { userHistory } = ThunkAPI.getState();

    let searchInput = {...userHistory.searchInput};
    for (const input of payload) {
      searchInput[input.name] = input.value;
    }

    ThunkAPI.dispatch(searchHistory(searchInput));

    return searchInput;
  }
);

export const searchHistory = createAsyncThunk(
  'userHistory/search',
  async (payload, ThunkAPI) => {
    const { userHistory } = ThunkAPI.getState();

    const apiResponse = await applicationApi.search(payload ?? userHistory.searchInput);

    return {
      rows: apiResponse.data.data.rows,
      totalCount: apiResponse.data.data.totalCount,
    };
  }
);

export const dispatchPaymentMethodInput = createAsyncThunk(
  'userHistory/paymentmethod/set',
  async (payload, ThunkAPI) => {
    const { userHistory } = ThunkAPI.getState();

    let paymentMethodInput = {...userHistory.selectedRow.paymentMethodInput};
    for (const input of payload.paymentMethodInput) {
      paymentMethodInput[input.name] = input.value;
    }

    if (payload.startUpdate) {
      ThunkAPI.dispatch(updatePaymentDetails(paymentMethodInput));
    }

    return paymentMethodInput;
  }
);

export const updatePaymentDetails = createAsyncThunk(
  'userApplication/paymentinput/set',
  async (payload, ThunkAPI) => {
    const apiResponse = await applicationApi.updatePaymentDetails(payload);

    const { app, userHistory } = ThunkAPI.getState();

    if (userHistory.selectedRow.paymentMethodInput.paymentMethod === AppConstants.APPLICATION_PAYMENT_METHOD.CCP.ID) {
      ThunkAPI.dispatch(handleCcpPaymentCreate({
        netId: app.me.user?.userId,
        applicationId: userHistory.selectedRow.paymentMethodInput.applicationId,
      }));
    }
    
    if (userHistory.selectedRow.paymentMethodInput.paymentMethod === AppConstants.APPLICATION_PAYMENT_METHOD.OLPP.ID) {
      ThunkAPI.dispatch(handleOlppPaymentCreate({
        netId: app.me.user?.userId,
        applicationId: userHistory.selectedRow.paymentMethodInput.applicationId,
      }));
    }
    else ThunkAPI.dispatch(searchHistory());

    return apiResponse.data;
  }
);

export const updatePhoto = createAsyncThunk(
  'userApplication/photo/update',
  async (payload, ThunkAPI) => {
    const apiResponse = await applicationApi.updatePhoto(payload);
    ThunkAPI.dispatch(searchHistory());
    return apiResponse.data;
  }
);

export const userHistorySlice = createSlice(
  {
    name: 'userHistory',
    initialState,
    reducers: {
      resetStore: () => initialState,
      setIsManagePaymentDialogOpen: (state, action) => {
        state.isManagePaymentDialogOpen = action.payload;
      },
      setIsPayInPersonDialogOpen: (state, action) => {
        state.isPayInPersonDialogOpen = action.payload;
      },
      setIsCCPDialogOpen: (state, action) => {
        state.isCCPDialogOpen = action.payload;
      },
      setRowDetails: (state, action) => {
        state.selectedRow.details = action.payload;
      },
      setCCPUrl: (state, action) => {
        state.selectedRow.ccpUrl = action.payload;
      },
      setPhotoInput: (state, action) => {
        state.selectedRow.photoInput = action.payload;
      },
      resetPhotoInput: (state, action) => {
        state.selectedRow.photoInput = initialState.selectedRow.photoInput;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(dispatchSearchInput.fulfilled, (state, action) => {
          state.searchInput = action.payload;
        })
        .addCase(searchHistory.fulfilled, (state, action) => {
          state.isLoading = false;
          state.searchResult.rows = action.payload.rows;
          state.searchResult.totalCount = action.payload.totalCount;

          state.selectedRow.paymentMethodInput = initialState.selectedRow.paymentMethodInput;
        })
        .addCase(searchHistory.rejected, (state, action) => {
          state.isLoading = false;
          state.searchResult.rows = [];
          state.searchResult.totalCount = 0;

          state.selectedRow.paymentMethodInput = initialState.selectedRow.paymentMethodInput;
        })
        .addCase(dispatchPaymentMethodInput.fulfilled, (state, action) => {
          state.selectedRow.paymentMethodInput = action.payload;
        })
        .addCase(updatePaymentDetails.fulfilled, (state, action) => {
          state.isLoading = false;
          if (state.selectedRow.paymentMethodInput.paymentMethod === AppConstants.APPLICATION_PAYMENT_METHOD.INPERSON.ID) {
            state.isPayInPersonDialogOpen = true;
          }
        })
        .addCase(handleCcpPaymentCreate.fulfilled, (state, action) => {
          state.isLoading = false;
          const ccpUrl = action.payload;
          state.selectedRow.ccpUrl = ccpUrl;
          state.isCCPDialogOpen = true;
        })
        .addCase(updatePhoto.fulfilled, (state, action) => {
          state.isLoading = false;
          state.selectedRow.photoInput = initialState.selectedRow.photoInput;
        })
        .addMatcher(isAnyOf(
            searchHistory.pending,
            updatePaymentDetails.pending,
            handleCcpPaymentCreate.pending,
            handleOlppPaymentCreate.pending,
            handleOlppPaymentCreate.pending,
            updatePhoto.pending,
          ), (state, action) => {
          state.isLoading = true;
        })
        .addMatcher(isAnyOf(
            handleOlppPaymentCreate.fulfilled,
            updatePaymentDetails.rejected,
            handleCcpPaymentCreate.rejected,
            handleOlppPaymentCreate.rejected,
            handleOlppPaymentCreate.rejected,
            updatePhoto.rejected,
          ), (state, action) => {
          state.isLoading = false;
        })
    }
  }
);

export default userHistorySlice;